import { useCallback, useEffect, useRef, useState } from 'react';
import { explain } from './supabase';

/**
 * Runs an async loader and keeps { loading, error, reload } alongside the data.
 *
 * The point of this hook is that there is no way to accidentally ignore an
 * error: anything the loader throws lands in `error` and the calling screen
 * renders it. That is the discipline the old codebase was missing.
 */
export function useAsync(loader, deps = []) {
  const [state, setState] = useState({ data: null, loading: true, error: null });
  const runIdRef = useRef(0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableLoader = useCallback(loader, deps);

  const run = useCallback(async () => {
    const runId = ++runIdRef.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await stableLoader();
      if (runId === runIdRef.current) setState({ data, loading: false, error: null });
    } catch (err) {
      if (runId === runIdRef.current) {
        console.error(err);
        setState({ data: null, loading: false, error: explain(err) });
      }
    }
  }, [stableLoader]);

  useEffect(() => {
    run();
  }, [run]);

  return { ...state, reload: run, setData: (d) => setState((s) => ({ ...s, data: d })) };
}
